import { frontendURL } from 'dashboard/helper/URLHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import store from 'dashboard/store';
import AppContainer from 'dashboard/routes/dashboard/Dashboard.vue';
import SettingsContent from 'dashboard/routes/dashboard/settings/Wrapper.vue';
import pluginMetadata from '../../../plugin.json';
import TiendaNubeIntegration from './routes/settings/integrations/TiendaNube.vue';
import TiendaNubeOrdersIndex from './routes/nuvemshop/TiendaNubeOrdersIndex.vue';
import TiendaNubeAbandonedCheckoutsIndex from './routes/nuvemshop/TiendaNubeAbandonedCheckoutsIndex.vue';

const automationContextKey = pluginMetadata.config.AUTOMATION_CONTEXT_KEY;
const automationContextLabel = pluginMetadata.config.AUTOMATION_CONTEXT_LABEL;
const automationContextIcon = pluginMetadata.config.AUTOMATION_CONTEXT_ICON;

const automationExtension = {
  entityAttributes: {
    contact: {
      tienda_nube_event_name: {
        name: 'TIENDA_NUBE_EVENT_NAME',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
      tienda_nube_order_id: {
        name: 'TIENDA_NUBE_ORDER_ID',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
      tienda_nube_order_number: {
        name: 'TIENDA_NUBE_ORDER_NUMBER',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
      tienda_nube_order_status: {
        name: 'TIENDA_NUBE_ORDER_STATUS',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
      tienda_nube_payment_status: {
        name: 'TIENDA_NUBE_PAYMENT_STATUS',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
      tienda_nube_total: {
        name: 'TIENDA_NUBE_TOTAL',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
      tienda_nube_currency: {
        name: 'TIENDA_NUBE_CURRENCY',
        inputType: 'plain_text',
        filterOperators: [
          { value: 'equal_to', label: 'Equal to' },
          { value: 'not_equal_to', label: 'Not equal to' },
          { value: 'contains', label: 'Contains' },
          { value: 'does_not_contain', label: 'Does not contain' },
          { value: 'is_present', label: 'Is present' },
          { value: 'is_not_present', label: 'Is not present' },
        ],
      },
    },
  },
  eventContexts: {
    [automationContextKey]: {
      label: automationContextLabel,
      icon: automationContextIcon,
      events: pluginMetadata.config.AUTOMATION_EVENT_KEYS,
    },
  },
  eventEntities: pluginMetadata.config.AUTOMATION_EVENT_KEYS.reduce(
    (result, key) => ({
      ...result,
      [key]: ['contact'],
    }),
    {}
  ),
  actionsByEntity: {
    contact: [
      'send_webhook_event',
      'delay_next_action',
      'delay_next_action_range',
    ],
  },
  automationRuleEvents: pluginMetadata.config.AUTOMATION_EVENT_KEYS.map(
    key => ({
      key,
      value: key.toUpperCase(),
      context: automationContextKey,
    })
  ),
};

const ensurePluginState = () => {
  window.chatwoot = window.chatwoot || {};
  window.chatwoot.plugins = window.chatwoot.plugins || {};
  window.chatwoot.plugins['tienda-nube'] =
    window.chatwoot.plugins['tienda-nube'] || {};

  return window.chatwoot.plugins['tienda-nube'];
};

const mergeAutomationRuleEvents = (baseEvents = [], extensionEvents = []) => {
  const seen = new Map();
  [...baseEvents, ...extensionEvents].forEach(event => {
    if (event?.key) {
      seen.set(event.key, event);
    }
  });

  return Array.from(seen.values());
};

const getTiendaNubeIntegration = () => {
  const integrationGetter = store.getters['integrations/getIntegration'];
  return integrationGetter ? integrationGetter('tienda_nube', {}) : null;
};

const hasActiveTiendaNubeConnection = () => {
  const integration = getTiendaNubeIntegration();
  return Boolean(
    Array.isArray(integration?.hooks) && integration.hooks.length > 0
  );
};

const registerAutomationExtension = PluginSystem => {
  const {
    contact: automationContactAttributes = {},
    ...automationEntityAttributes
  } = automationExtension.entityAttributes || {};

  if (!PluginSystem?.registerHook) {
    return;
  }

  PluginSystem.registerHook('automation-enhanced:config', config => {
    if (!hasActiveTiendaNubeConnection()) {
      return config;
    }

    return {
      ...config,
      entityAttributes: {
        ...config.entityAttributes,
        ...automationEntityAttributes,
        contact: {
          ...(config.entityAttributes?.contact || {}),
          ...automationContactAttributes,
        },
      },
      eventContexts: {
        ...config.eventContexts,
        ...automationExtension.eventContexts,
      },
      eventEntities: {
        ...config.eventEntities,
        ...automationExtension.eventEntities,
      },
      actionsByEntity: {
        ...config.actionsByEntity,
        ...automationExtension.actionsByEntity,
        contact: [
          ...new Set([
            ...(config.actionsByEntity?.contact || []),
            ...(automationExtension.actionsByEntity?.contact || []),
          ]),
        ],
      },
      automationRuleEvents: mergeAutomationRuleEvents(
        config.automationRuleEvents,
        automationExtension.automationRuleEvents
      ),
    };
  });
};

const registerIntegrationRoute = () => {
  const router = window.chatwootRouter;
  if (
    !router?.addRoute ||
    router?.hasRoute?.('settings_integrations_tienda_nube')
  ) {
    return;
  }

  router.addRoute({
    path: frontendURL('accounts/:accountId'),
    component: AppContainer,
    children: [
      {
        path: 'settings/integrations/tienda_nube',
        component: SettingsContent,
        props: {
          headerTitle: 'INTEGRATION_SETTINGS.HEADER',
          icon: 'flash-on',
          showBackButton: true,
          backUrl: { name: 'settings_applications' },
        },
        children: [
          {
            path: '',
            name: 'settings_integrations_tienda_nube',
            component: TiendaNubeIntegration,
            meta: {
              featureFlag: FEATURE_FLAGS.INTEGRATIONS,
              permissions: ['administrator'],
            },
            props: route => ({ error: route.query.error }),
          },
        ],
      },
      {
        path: 'nuvemshop/orders',
        name: 'tienda_nube_orders',
        component: TiendaNubeOrdersIndex,
        meta: {
          permissions: ['administrator'],
        },
      },
      {
        path: 'nuvemshop/abandoned-checkouts',
        name: 'tienda_nube_abandoned_checkouts',
        component: TiendaNubeAbandonedCheckoutsIndex,
        meta: {
          permissions: ['administrator'],
        },
      },
    ],
  });
};

const rematchCurrentRoute = () => {
  const router = window.chatwootRouter;
  const currentRoute = router?.currentRoute?.value;

  if (!router?.replace || !currentRoute) {
    return;
  }

  const isGenericIntegrationRoute =
    currentRoute.name === 'settings_applications_integration' &&
    currentRoute.params?.integration_id === 'tienda_nube';
  const isTiendaNubePath = currentRoute.path?.includes(
    '/settings/integrations/tienda_nube'
  );

  if (!isGenericIntegrationRoute && !isTiendaNubePath) {
    return;
  }

  const accountId =
    currentRoute.params?.accountId || currentRoute.params?.account_id;
  if (!accountId) {
    return;
  }

  Promise.resolve().then(() =>
    router
      .replace({
        name: 'settings_integrations_tienda_nube',
        params: { accountId },
        query: currentRoute.query,
        hash: currentRoute.hash,
      })
      .catch(() => {})
  );
};

export default {
  install(app, { PluginSystem } = {}) {
    const pluginState = ensurePluginState();
    pluginState.config = pluginMetadata.config;

    registerIntegrationRoute();
    rematchCurrentRoute();
    registerAutomationExtension(PluginSystem);

    if (PluginSystem?.registerHook) {
      PluginSystem.registerHook('sidebar:menu-items', payload => {
        if (!payload?.items || !Array.isArray(payload.items)) {
          return payload;
        }

        const integration = getTiendaNubeIntegration();
        if (
          !Array.isArray(integration?.hooks) ||
          integration.hooks.length === 0
        ) {
          return payload;
        }

        const exists = payload.items.some(item => item.name === 'Nuvemshop');
        if (exists) {
          return payload;
        }

        payload.items.push({
          name: 'Nuvemshop',
          label: payload.t?.('TIENDA_NUBE_PAGES.SIDEBAR_TITLE') || 'Nuvemshop',
          icon: 'i-lucide-shopping-bag',
          children: [
            {
              name: 'Nuvemshop Orders',
              label:
                payload.t?.('TIENDA_NUBE_PAGES.SIDEBAR_ORDERS') || 'Pedidos',
              icon: 'i-lucide-receipt-text',
              activeOn: ['tienda_nube_orders'],
              to: payload.accountScopedRoute('tienda_nube_orders'),
            },
            {
              name: 'Nuvemshop Abandoned Checkouts',
              label:
                payload.t?.('TIENDA_NUBE_PAGES.SIDEBAR_ABANDONED') ||
                'Carrinhos abandonados',
              icon: 'i-lucide-shopping-cart',
              activeOn: ['tienda_nube_abandoned_checkouts'],
              to: payload.accountScopedRoute('tienda_nube_abandoned_checkouts'),
            },
          ],
        });

        return payload;
      });
    }

    app.component('TiendaNubeIntegration', TiendaNubeIntegration);
    app.component('TiendaNubeOrdersIndex', TiendaNubeOrdersIndex);
    app.component(
      'TiendaNubeAbandonedCheckoutsIndex',
      TiendaNubeAbandonedCheckoutsIndex
    );

    store
      .dispatch('integrations/get')
      .then(() => {
        window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
      })
      .catch(() => {});
  },
};
